/* Kemper Tools Theme - Global JavaScript */

class KemperTheme {
  constructor() {
    this.handleLegacyAboutRoute();
    this.init();
  }

  init() {
    this.initSmoothScroll();
    this.initMobileMenu();
    this.initScrollEffects();
    this.initLazyLoading();
  }

  handleLegacyAboutRoute() {
    const normalizedLegacyAbout =
      window.location.pathname === '/pages/about' ||
      window.location.pathname === '/pages/about/' ||
      window.location.pathname.endsWith('/pages/about/');

    if (normalizedLegacyAbout) {
      window.location.replace(`${window.location.origin}/pages/about-us`);
    }
  }

  // Smooth scroll for anchor links
  initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#') return;

        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // Mobile menu toggle
  initMobileMenu() {
    const menuToggle = document.querySelector('.kemper-nav__mobile-toggle');
    const mobileMenu = document.querySelector('.kemper-nav__mobile-menu');
    const closeBtn = document.querySelector('.kemper-nav__mobile-close');
    const menuOverlay = document.querySelector('.kemper-nav__mobile-overlay');

    const closeMenu = () => {
      if (menuToggle) menuToggle.setAttribute('aria-expanded', 'false');
      if (mobileMenu) mobileMenu.classList.remove('is-open');
      document.body.classList.remove('menu-open');
    };

    const openMenu = () => {
      if (menuToggle) menuToggle.setAttribute('aria-expanded', 'true');
      if (mobileMenu) mobileMenu.classList.add('is-open');
      document.body.classList.add('menu-open');
    };

    if (menuToggle && mobileMenu) {
      menuToggle.addEventListener('click', () => {
        const isExpanded = menuToggle.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
          closeMenu();
        } else {
          openMenu();
        }
      });

      // Close button inside mobile menu
      if (closeBtn) {
        closeBtn.addEventListener('click', closeMenu);
      }

      // Close menu when tapping overlay (outside the drawer)
      if (menuOverlay) {
        menuOverlay.addEventListener('click', closeMenu);
      }

      // Close menu when clicking any link inside it
      mobileMenu.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', closeMenu);
      });

      // Close menu on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
          closeMenu();
        }
      });
    }
  }

  // Header scroll effects
  initScrollEffects() {
    const header = document.querySelector('.kemper-header');
    if (!header) return;

    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateHeader = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > 100) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }

      // Keep header always visible (sticky)
      header.classList.remove('is-hidden');

      lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      }
    });
  }

  // Lazy loading for images
  initLazyLoading() {
    if ('IntersectionObserver' in window) {
      const lazyImages = document.querySelectorAll('img[data-src]');

      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            observer.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px 0px'
      });

      lazyImages.forEach(img => imageObserver.observe(img));
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.kemperTheme = new KemperTheme();
});

// Cart functionality — fast client-side rendering, no full-page fetches
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('.cart-drawer');
    this.overlay = document.querySelector('.cart-drawer__overlay');
    this.updating = false;
    if (this.drawer) this.init();
  }

  init() {
    // Cart toggle buttons
    document.querySelectorAll('.cart-toggle').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close button (delegated so it survives innerHTML updates)
    this.drawer.addEventListener('click', (e) => {
      if (e.target.closest('.cart-drawer__close')) {
        this.closeDrawer();
        return;
      }
      if (this.updating) return;

      const qtyBtn = e.target.closest('.cart-drawer__qty-btn');
      const removeBtn = e.target.closest('.cart-drawer__remove');

      if (qtyBtn) {
        const line = parseInt(qtyBtn.dataset.line);
        const action = qtyBtn.dataset.action;
        const item = qtyBtn.closest('.cart-drawer__item');
        const currentQty = parseInt(item.querySelector('.cart-drawer__qty-value').textContent);
        const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
        this.changeQuantity(line, newQty, item);
      }

      if (removeBtn) {
        const line = parseInt(removeBtn.dataset.line);
        const item = removeBtn.closest('.cart-drawer__item');
        this.changeQuantity(line, 0, item);
      }
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeDrawer());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && document.body.classList.contains('cart-open')) {
        this.closeDrawer();
      }
    });

    // Auto-open cart drawer if redirected from /cart
    if (new URLSearchParams(window.location.search).has('open_cart')) {
      this.open();
      var cleanUrl = window.location.href.replace(/[?&]open_cart=1/, '').replace(/\?$/, '');
      window.history.replaceState(null, '', cleanUrl);
    }
  }

  open() {
    this.drawer.classList.add('is-open');
    document.body.classList.add('cart-open');
  }

  closeDrawer() {
    this.drawer.classList.remove('is-open');
    document.body.classList.remove('cart-open');
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  async changeQuantity(line, quantity, itemEl) {
    if (this.updating) return;
    this.updating = true;

    // Optimistic UI — instant feedback
    if (itemEl) {
      if (quantity === 0) {
        itemEl.style.opacity = '0.3';
        itemEl.style.pointerEvents = 'none';
      } else {
        const qtyEl = itemEl.querySelector('.cart-drawer__qty-value');
        if (qtyEl) qtyEl.textContent = quantity;
        itemEl.style.opacity = '0.6';
      }
    }

    try {
      // Single request — returns updated cart JSON
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Cart update error:', err);
      // Recover by fetching current cart
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        this.renderCart(cart);
      } catch (e) { /* silent */ }
    } finally {
      this.updating = false;
    }
  }

  // Rebuild drawer from cart JSON — no page fetch needed
  renderCart(cart) {
    // Update all cart count badges
    const count = cart.item_count;
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.dataset.cartCount = count;
      if (el.classList.contains('kemper-nav__cart-count')) {
        el.textContent = count > 0 ? count : '';
      } else {
        el.textContent = '(' + count + ')';
      }
    });

    const body = this.drawer.querySelector('.cart-drawer__body');
    let footer = this.drawer.querySelector('.cart-drawer__footer');

    if (count === 0) {
      body.innerHTML =
        '<div class="cart-drawer__empty">' +
          '<p>Your cart is empty..</p>' +
          '<a href="/collections/all" class="cart-drawer__continue">Continue shopping</a>' +
        '</div>';
      if (footer) footer.remove();
      return;
    }

    // Build items HTML
    let html = '<div class="cart-drawer__items">';
    cart.items.forEach((item, i) => {
      const ln = i + 1;
      const imgSrc = item.image ? item.image + (item.image.indexOf('?') !== -1 ? '&' : '?') + 'width=160' : '';
      const showVariant = item.variant_title && item.variant_title !== 'Default Title';
      const showCompare = item.original_price > item.final_price;
      const title = (item.product_title || '').replace(/"/g, '&quot;');

      html +=
        '<div class="cart-drawer__item" data-line="' + ln + '">' +
          '<div class="cart-drawer__item-image">' +
            (imgSrc ? '<img src="' + imgSrc + '" alt="' + title + '" width="80" height="80" loading="lazy">' : '') +
          '</div>' +
          '<div class="cart-drawer__item-details">' +
            '<a href="' + item.url + '" class="cart-drawer__item-title">' + item.product_title + '</a>' +
            (showVariant ? '<p class="cart-drawer__item-variant">' + item.variant_title + '</p>' : '') +
            '<div class="cart-drawer__item-pricing">' +
              (showCompare ? '<span class="cart-drawer__item-compare">' + this.formatMoney(item.original_price) + '</span>' : '') +
              '<span class="cart-drawer__item-price">' + this.formatMoney(item.final_price) + '</span>' +
            '</div>' +
            '<div class="cart-drawer__item-actions">' +
              '<div class="cart-drawer__quantity">' +
                '<button class="cart-drawer__qty-btn" data-action="decrease" data-line="' + ln + '" aria-label="Decrease quantity">\u2212</button>' +
                '<span class="cart-drawer__qty-value">' + item.quantity + '</span>' +
                '<button class="cart-drawer__qty-btn" data-action="increase" data-line="' + ln + '" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<button class="cart-drawer__remove" data-line="' + ln + '" aria-label="Remove ' + title + '">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                  '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>' +
                '</svg>' +
              '</button>' +
            '</div>' +
          '</div>' +
        '</div>';
    });
    html += '</div>';
    body.innerHTML = html;

    // Update or create footer
    if (!footer) {
      footer = document.createElement('div');
      footer.className = 'cart-drawer__footer';
      this.drawer.appendChild(footer);
    }
    footer.innerHTML =
      '<div class="cart-drawer__total">' +
        '<span>Estimated total</span>' +
        '<span class="cart-drawer__total-price">' + this.formatMoney(cart.total_price) + '</span>' +
      '</div>' +
      '<a href="/cart/checkout" class="cart-drawer__checkout">Checkout</a>';
  }

  // Refresh from /cart.js — used after add-to-cart
  async refreshDrawer() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Cart refresh error:', err);
    }
  }
}

// Initialize cart drawer
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});

// Intercept all add-to-cart form submissions → AJAX + open drawer
document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form || !form.action || !form.action.includes('/cart/add')) {
      if (!form || !form.classList.contains('product-card__form')) return;
    }

    e.preventDefault();
    e.stopPropagation();

    const btn = form.querySelector('button[type="submit"]');
    const originalText = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'ADDING...';
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      const data = await response.json();

      if (data.status === 422) {
        if (btn) {
          btn.textContent = data.description || 'Error';
          setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);
        }
        return;
      }

      // Open drawer + refresh from lightweight /cart.js
      if (window.cartDrawer) {
        window.cartDrawer.open();
        await window.cartDrawer.refreshDrawer();
      }

      if (btn) {
        btn.textContent = 'ADDED!';
        setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 1500);
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      if (btn) { btn.textContent = originalText; btn.disabled = false; }
    }
  });
});
